import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppStore } from '../../../store';
import { MaintenanceTable } from '../../Layouts/DataTable';
import { useApiError } from '../../../hooks';
import { EDataTableType, EModuleType, EApiType } from '../../../data/Constants';
import { fetchMultiNumericFeatures } from '../../../store/states/FeatureSlice';
import { FeatureApi } from '../../../api/FeatureApi';
import { joinSelectedKeys } from '../../../services/DataHelperFunctions';
import { setAlertMessage } from '../../../store/states/AlertMessageSlice';

/**
 * This component renders the data table to show the Multi-Numeric features
 * @returns {React.ReactElement} returns the Data table with multi numeric rows
 */
export const MultiNumericManagement = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const errorElement = useApiError(EApiType.MultiNumericFeatureKey);

  /**
   * This method trigger when user click on the delete button on the data table
   * This method calls the delete api to delete the selected multi numeric rows
   * @param {string [] } request the selected row id list
   * @returns {void}
   */
  const deleteItems = async (request: string[]): Promise<void> => {
    const multiNumericFeature = AppStore.getState().feature.multiNumericFeature;

    const delKeys = joinSelectedKeys(request);

    const response = await FeatureApi.deleteMultiNumericFeatures({ request });
    if (response?.data) {
      const message = t('messages.success.delete.multiNumeric', {
        length: request.length,
        list: delKeys,
      });
      dispatch(setAlertMessage({ show: true, message: message, type: 'success' }));
      dispatch(
        fetchMultiNumericFeatures({
          page: multiNumericFeature.currentPage,
          limit: multiNumericFeature.recordsPerPage,
          searchCode: multiNumericFeature.searchKey,
        })
      );
    } else {
      const message = t('messages.fail.delete.multiNumeric', {
        length: delKeys.length,
        list: delKeys,
      });
      dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
    }
  };

  if (errorElement) return errorElement;

  return (
    <>
      <h3 className="mainHeader"> {t('labels.multiNumericManagement')}</h3>
      <MaintenanceTable
        type={EDataTableType.MultiNumericFeature}
        getDataList={fetchMultiNumericFeatures}
        moduleType={EModuleType.Feature}
        deleteItems={deleteItems}
      />
    </>
  );
};
